import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getConfig } from "../api/client";
import type { SiteConfig } from "../api/types";

interface ConfigState {
  config: SiteConfig | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const ConfigContext = createContext<ConfigState | null>(null);

/** Everything the site renders about the launch comes from GET /api/config. */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getConfig()
      .then((next) => {
        if (!live) return;
        setConfig(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setError(cause instanceof Error ? cause.message : "Could not load site configuration.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const value = useMemo<ConfigState>(
    () => ({ config, loading, error, reload }),
    [config, loading, error, reload],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigState {
  const value = useContext(ConfigContext);
  if (!value) throw new Error("useConfig must be used inside ConfigProvider");
  return value;
}
